import { useState, useEffect } from 'react'
import { X, Crown, Sparkles, Check, Gift } from 'lucide-react'

const API_BASE = import.meta.env.VITE_API_URL || ''

export default function FoundingMemberPopup({ userEmail, onClose, onClaimed }) {
  const [loading, setLoading] = useState(false)
  const [claimed, setClaimed] = useState(false)
  const [error, setError] = useState(null)
  const [spotsRemaining, setSpotsRemaining] = useState(4)
  const [benefits, setBenefits] = useState([])

  useEffect(() => {
    // Fetch current status
    fetchStatus()
  }, [])

  const fetchStatus = async () => {
    try {
      const res = await fetch(`${API_BASE}/founding/check-eligibility/${encodeURIComponent(userEmail)}`)
      const data = await res.json()
      if (data.spotsRemaining) setSpotsRemaining(data.spotsRemaining)
      if (data.benefits) setBenefits(data.benefits)
    } catch (err) {
      console.error('Failed to fetch founding status:', err)
    }
  }

  const handleClaim = async () => {
    setLoading(true)
    setError(null)
    
    try {
      const res = await fetch(`${API_BASE}/founding/claim`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail })
      })
      
      const data = await res.json()
      
      if (!res.ok) {
        throw new Error(data.error || 'Failed to claim spot')
      }
      
      setClaimed(true)
      if (onClaimed) onClaimed(data)
      
      // Auto-close after showing success
      setTimeout(() => {
        onClose()
        window.location.reload() // Refresh to show new plan
      }, 3000)
      
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  const handleDismiss = async () => {
    try {
      await fetch(`${API_BASE}/founding/dismiss-popup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: userEmail })
      })
    } catch (err) {
      console.error('Failed to dismiss popup:', err)
    }
    onClose()
  }

  if (claimed) {
    return (
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
        <div className="bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 rounded-2xl p-8 max-w-md w-full shadow-2xl border border-purple-500/30 text-center">
          <div className="w-20 h-20 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <Crown className="w-10 h-10 text-purple-900" />
          </div>
          
          <h2 className="text-3xl font-bold text-white mb-4">
            🎉 Welcome, Founding Member!
          </h2>
          
          <p className="text-purple-200 text-lg mb-6">
            You're now part of Zenna history! Your Creator tier benefits are active forever.
          </p>
          
          <div className="flex items-center justify-center gap-2 text-yellow-400">
            <Sparkles className="w-5 h-5" />
            <span className="text-sm">Redirecting to your dashboard...</span>
            <Sparkles className="w-5 h-5" />
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm flex items-center justify-center z-50 p-4">
      <div className="bg-gradient-to-br from-purple-900 via-purple-800 to-indigo-900 rounded-2xl p-6 md:p-8 max-w-lg w-full shadow-2xl border border-purple-500/30 relative overflow-hidden">
        {/* Sparkle decorations */}
        <div className="absolute top-4 left-4 text-yellow-400 animate-pulse">✨</div>
        <div className="absolute top-8 right-8 text-yellow-400 animate-pulse delay-100">✨</div>
        <div className="absolute bottom-12 left-8 text-yellow-400 animate-pulse delay-200">✨</div>
        <div className="absolute bottom-4 right-4 text-yellow-400 animate-pulse delay-300">✨</div>
        
        {/* Close button */}
        <button
          onClick={handleDismiss}
          className="absolute top-4 right-4 text-purple-300 hover:text-white transition-colors"
        >
          <X className="w-6 h-6" />
        </button>
        
        {/* Header */}
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-amber-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-yellow-500/30">
            <Crown className="w-8 h-8 text-purple-900" />
          </div>
          
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-2">
            🎁 You're One of the First!
          </h2>
          
          <p className="text-purple-200">
            Claim your <span className="text-yellow-400 font-semibold">Founding Member</span> spot
          </p>
        </div>
        
        {/* Spots remaining badge */}
        <div className="bg-purple-950/50 rounded-xl p-4 mb-6 border border-purple-500/20">
          <div className="flex items-center justify-between">
            <span className="text-purple-200">Spots Remaining:</span>
            <span className="text-2xl font-bold text-yellow-400">{spotsRemaining} of 7</span>
          </div>
          <div className="mt-2 h-2 bg-purple-950 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-yellow-400 to-amber-500 transition-all duration-500"
              style={{ width: `${((7 - spotsRemaining) / 7) * 100}%` }}
            />
          </div>
        </div>
        
        {/* Benefits list */}
        <div className="mb-6">
          <h3 className="text-white font-semibold mb-3 flex items-center gap-2">
            <Gift className="w-5 h-5 text-yellow-400" />
            Your FREE Lifetime Benefits:
          </h3>
          <ul className="space-y-2">
            {(benefits.length > 0 ? benefits.slice(0, 6) : [
              'Creator tier features FREE FOR LIFE',
              '400 credits/month (never expires)',
              'All export formats including embroidery',
              'Full Color Tools & Text Tool access',
              'Founding Member badge',
              'Name on Founders Wall'
            ]).map((benefit, i) => (
              <li key={i} className="flex items-start gap-2 text-purple-100 text-sm">
                <Check className="w-4 h-4 text-green-400 mt-0.5 flex-shrink-0" />
                <span>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Error message */}
        {error && (
          <div className="bg-red-500/20 border border-red-500/30 rounded-lg p-3 mb-4 text-red-200 text-sm">
            {error}
          </div>
        )}
        
        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <button
            onClick={handleClaim}
            disabled={loading}
            className="flex-1 bg-gradient-to-r from-yellow-400 to-amber-500 hover:from-yellow-300 hover:to-amber-400 text-purple-900 font-bold py-3 px-6 rounded-xl transition-all duration-200 transform hover:scale-[1.02] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-yellow-500/30"
          >
            {loading ? (
              <>
                <div className="w-5 h-5 border-2 border-purple-900/30 border-t-purple-900 rounded-full animate-spin" />
                Claiming...
              </>
            ) : (
              <>
                <Crown className="w-5 h-5" />
                Claim My Spot!
              </>
            )}
          </button>
          
          <button
            onClick={handleDismiss}
            className="sm:w-auto px-6 py-3 text-purple-300 hover:text-white transition-colors text-sm"
          >
            Maybe Later
          </button>
        </div>
        
        {/* Fine print */}
        <p className="text-purple-400 text-xs text-center mt-4">
          This is a one-time offer for early supporters. Once spots are filled, this offer won't return.
        </p>
      </div>
    </div>
  )
}